import React, { useEffect, useState, useMemo } from "react";
import axios from "../../../utills/api";
import {
    Card,
    CardHeader,
    CardBody,
    Button,
    Input,
    Row,
    Col,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
} from "reactstrap";
import { toast } from "react-toastify";
import TableContainer from "../../../Components/Common/TableContainer";
import { Link } from "react-router-dom";
import MenuCategoryModal from "./CategoryModal/MenuCategoryModal";
import { transliterateHindi } from "../../../utills/transliterate";

// ⛔ remove hardcoded categories
const types = ["Veg", "Non-Veg"];
const API_BASE = "products";
const CATEGORY_API = "menu/menu-settings";

const Menu = () => {
    document.title = "HMS | Menu";
    const [search, setSearch] = useState("");
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]); // ✅ fetched categories
    const [modalOpen, setModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [newProduct, setNewProduct] = useState({
        image: "",
        category: "",
        type: "",
        name: {
            en: "",
            hi: ""
        },
        price: "",
        publishedOn: "",
        status: "Active",
    });
    const [
        categoryModal,
        setCategoryModal
    ] = useState(false);
    const toggleModal = () => setModalOpen(!modalOpen);
    const toggleCategoryModal = () =>
        setCategoryModal((prev) => !prev);
    const IMAGE_BASE_URL = axios.defaults.baseURL.replace("/api", "");

    // ✅ Fetch categories from API
    const fetchCategories = async () => {
        try {
            const res = await axios.get(CATEGORY_API);
            const data = Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : [];
            setCategories(data);
        } catch (err) {
            console.error("Failed to fetch categories:", err);
            toast.error("Failed to fetch categories");
        }
    };

    // ✅ Fetch products
    const fetchProducts = async () => {
        try {
            const res = await axios.get(API_BASE);
            const data = Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : [];
            setProducts(data);
        } catch (err) {
            console.error("Failed to fetch products:", err);
            toast.error("Failed to fetch products");
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewProduct({ ...newProduct, [name]: value });
    };

    // ✅ Add or Update product (unchanged, kept same as yours)
    const handleAddOrUpdateProduct = async () => {
        // Validate required fields
        if (
            !newProduct.name.en ||
            !newProduct.category ||
            !newProduct.price ||
            !newProduct.publishedOn
        ) {
            toast.error(
                "Please fill all required fields"
            );
            return;
        }
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Unauthorized: No token found");
                return;
            }

            const headers = {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
            };

            const formData = new FormData();
            formData.append("nameEn", newProduct.name.en);
            formData.append("nameHi", newProduct.name.hi);
            formData.append("category", newProduct.category);
            formData.append("price", newProduct.price);
            formData.append("discountedPrice", newProduct.discountedPrice || 0); // required by backend
            formData.append("publishedOn", newProduct.publishedOn);
            formData.append("status", newProduct.status);
            formData.append("type", newProduct.type);

            // ✅ Image handling: file or URL
            if (newProduct.image instanceof File) {
                formData.append("image", newProduct.image); // file upload
            } else if (typeof newProduct.image === "string" && newProduct.image.trim() !== "") {
                formData.append("image", newProduct.image); // URL
            }

            let res;
            if (editingProduct) {
                // Update existing product
                res = await axios.put(`${API_BASE}/${editingProduct._id}`, formData, { headers });
            } else {
                // Add new product
                res = await axios.post(API_BASE, formData, { headers });
            }

            const savedProduct = res.data;

            if (editingProduct) {
                setProducts((prev) =>
                    prev.map((p) => (p._id === editingProduct._id ? { ...p, ...savedProduct } : p))
                );
                toast.success("Product updated successfully");
            } else {
                setProducts((prev) => [...prev, savedProduct]);
                toast.success("Product added successfully");
            }

            // Reset form
            setNewProduct({
                image: "",
                category: "",
                type: "",
                name: {
                    en: "",
                    hi: ""
                },
                price: "",
                discountedPrice: "",
                publishedOn: "",
                status: "Active",
            });
            setEditingProduct(null);
            toggleModal();
        } catch (err) {
            console.error("Failed to save product:", err.response?.data || err.message);
            toast.error(err.response?.data?.message || "Failed to save product");
        }
    };

    // Delete product
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this product?")) return;
        try {
            await axios.delete(`${API_BASE}/${id}`);
            setProducts(products.filter((p) => p._id !== id));
            toast.success("Product deleted successfully");
        } catch (err) {
            console.error("Failed to delete product:", err);
            toast.error("Failed to delete product");
        }
    };

    // Edit product
    const handleEdit = (product) => {
        setEditingProduct(product);
        setNewProduct({
            image: product.image || "",
            category: product.category || "",
            type: product.type || "",
            name: {
                en:
                    product.name?.en ||
                    "",
                hi:
                    product.name?.hi ||
                    "",
            },
            price: product.price || "",
            publishedOn: product.publishedOn ? new Date(product.publishedOn).toISOString().split("T")[0] : "",
            status: product.status || "Active",
        });
        toggleModal();
    };


    const handleEnglishNameChange = async (e) => {
        const englishText = e.target.value;

        // Update English immediately
        setNewProduct((prev) => ({
            ...prev,
            name: {
                ...prev.name,
                en: englishText,
            },
        }));

        try {
            const hindiText =
                await transliterateHindi(
                    englishText
                );

            setNewProduct((prev) => ({
                ...prev,
                name: {
                    ...prev.name,
                    hi: hindiText,
                },
            }));
        } catch (err) {
            console.error(err);
        }
    };

    const filteredProducts = products.filter((item) => {
        const englishName =
            item?.name?.en || "";

        const hindiName =
            item?.name?.hi || "";

        return (
            englishName
                .toLowerCase()
                .includes(search.toLowerCase()) ||
            hindiName.includes(search)
        );
    });

    const productColumns = useMemo(
        () => [
            { header: "Product ID", accessorKey: "_id", cell: ({ getValue }) => getValue()?.slice(0, 5) },
            {
                header: "Image",
                accessorKey: "image",
                cell: ({ row }) => (
                    <img
                        src={
                            row.original.image?.startsWith("http")
                                ? row.original.image
                                : `${IMAGE_BASE_URL}${row.original.image}`
                        }
                        alt={row.original.name?.en}
                        width="50"
                        height="50"
                        className="rounded"
                    />

                ),
            },
            {
                header: "Name",
                cell: ({ row }) => (
                    <div>
                        <div>
                            {row.original.name?.en}
                        </div>

                        <small
                            className="text-muted"
                        >
                            {row.original.name?.hi}
                        </small>
                    </div>
                ),
            },
            { header: "Type", accessorKey: "type" },
            { header: "Category", accessorKey: "category" },
            { header: "Price (₹)", accessorKey: "price" },
            {
                header: "Published On",
                accessorKey: "publishedOn",
                cell: ({ cell }) =>
                    cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : "",
            },
            {
                header: "Status",
                accessorKey: "status",
                cell: ({ cell }) => (
                    <span
                        className={`badge bg-${cell.getValue() === "Active"
                            ? "badge text-uppercase bg-success-subtle text-success"
                            : "badge text-uppercase bg-danger-subtle text-danger"
                            }`}
                    >
                        {cell.getValue()}
                    </span>
                ),
            },
            {
                header: "Action",
                cell: ({ row }) => (
                    <td className="d-flex justify-content-center align-items-center gap-2">
                        <Link
                            onClick={() => handleEdit(row.original)}
                            className="btn btn-sm btn-soft-primary"
                            style={{ width: "28px", height: "28px" }}
                        >
                            <i className="ri-edit-line"></i>
                        </Link>
                        <Link
                            onClick={() => handleDelete(row.original._id)}
                            className="btn btn-sm btn-soft-danger"
                            style={{ width: "28px", height: "28px" }}
                        >
                            <i className="las la-trash-alt"></i>
                        </Link>
                    </td>
                ),
            },
        ],
        [products]
    );

    return (
        <>
            <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                    <h5 className="fs-16 mb-0">
                        Menu (Food & Beverages)
                    </h5>

                    <div
                        className="d-flex align-items-center"
                        style={{ gap: "10px" }}
                    >
                        <Row className="mb-0">
                            <Col md={12}>
                                <Input
                                    type="text"
                                    placeholder="Search products..."
                                    value={search}
                                    onChange={(e) =>
                                        setSearch(e.target.value)
                                    }
                                />
                            </Col>
                        </Row>

                        <Button
                            color="info"
                            onClick={toggleCategoryModal}
                        >
                            Create Food Category
                        </Button>

                        <Button
                            color="primary"
                            onClick={toggleModal}
                        >
                            + Add Product
                        </Button>
                    </div>
                </CardHeader>
                <CardBody>
                    <div className="table-responsive">
                        <TableContainer
                            columns={productColumns}
                            data={filteredProducts}
                            customPageSize={5}
                            isPagination
                            className="text-nowrap"
                        />
                    </div>
                </CardBody>
            </Card>

            {/* Modal for Add/Edit */}
            <Modal isOpen={modalOpen} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>
                    {editingProduct ? "Edit Product" : "Add Product"}
                </ModalHeader>
                <ModalBody>
                    <Form>
                        {/* Image URL */}
                        <FormGroup>
                            <Label>Image URL</Label>
                            <Input
                                type="text"
                                placeholder="Enter image URL (optional)"
                                name="image"
                                value={typeof newProduct.image === "string" ? newProduct.image : ""}
                                onChange={handleInputChange}
                                className="mb-2"
                            />
                        </FormGroup>

                        {/* File Upload */}
                        <FormGroup>
                            <Label>Or Upload Image</Label>
                            <Input
                                type="file"
                                name="image" // ✅ Must match Multer field
                                accept="image/*"
                                onChange={(e) =>
                                    setNewProduct({ ...newProduct, image: e.target.files[0] })
                                }
                            />
                        </FormGroup>

                        {/* Type */}
                        <FormGroup>
                            <Label>Type</Label>
                            <Input
                                type="select"
                                name="type"
                                value={newProduct.type}
                                onChange={handleInputChange}
                            >
                                <option value="">Select Type</option>
                                {types.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        {/* Category */}
                        <FormGroup>
                            <Label>Category</Label>
                            <Input
                                type="select"
                                name="category"
                                value={newProduct.category}
                                onChange={handleInputChange}
                            >
                                <option value="">Select Category</option>
                                {categories.map((cat) => (
                                    <option key={cat._id} value={cat.name}>{cat.name}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        {/* Name */}
                        <FormGroup>
                            <Label>Product Name (English)</Label>

                            <Input
                                type="text"
                                value={newProduct.name.en}
                                onChange={handleEnglishNameChange}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Product Name (Hindi)</Label>

                            <Input
                                type="text"
                                value={newProduct.name.hi}
                                readOnly
                            />
                        </FormGroup>
                        {/* Price */}
                        <FormGroup>
                            <Label>Price (₹)</Label>
                            <Input
                                type="number"
                                name="price"
                                value={newProduct.price}
                                onChange={handleInputChange}
                            />
                        </FormGroup>

                        {/* Discounted Price */}
                        <FormGroup>
                            <Label>Discounted Price (₹)</Label>
                            <Input
                                type="number"
                                name="discountedPrice"
                                value={newProduct.discountedPrice || ""}
                                onChange={handleInputChange}
                                placeholder="Optional"
                            />
                        </FormGroup>

                        {/* Published On */}
                        <FormGroup>
                            <Label>Published On</Label>
                            <Input
                                type="date"
                                name="publishedOn"
                                value={newProduct.publishedOn}
                                onChange={handleInputChange}
                            />
                        </FormGroup>

                        {/* Status */}
                        <FormGroup>
                            <Label>Status</Label>
                            <Input
                                type="select"
                                name="status"
                                value={newProduct.status}
                                onChange={handleInputChange}
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </Input>
                        </FormGroup>
                    </Form>
                </ModalBody>

                <ModalFooter>
                    <Button color="primary" onClick={handleAddOrUpdateProduct}>
                        {editingProduct ? "Update Product" : "Add Product"}
                    </Button>
                    <Button color="secondary" onClick={toggleModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
            <MenuCategoryModal
                isOpen={categoryModal}
                toggle={toggleCategoryModal}
            />
        </>
    );
};

export default Menu;
